import { useState, useMemo } from 'react';
import { useCabinet } from '../useCabinet';
import SectionTitle from '../SectionTitle';

const SCHEDULES = [
  {
    id: 'classic',
    title: 'Классический',
    badge: '5/2',
    desc: '20 дней · 8 ч/сут',
    icon: 'ri-sun-line',
    accent: 'amber',
    dayHours: 160,
    nightHours: 0,
  },
  {
    id: 'sixone',
    title: 'Один выходной',
    badge: '6/1',
    desc: '24 дня · 8 ч/сут',
    icon: 'ri-calendar-check-line',
    accent: 'emerald',
    dayHours: 192,
    nightHours: 0,
  },
  {
    id: 'night',
    title: 'Ночные смены',
    badge: '5/2',
    desc: '20 дней · 4 дн. + 4 ноч.',
    icon: 'ri-moon-line',
    accent: 'indigo',
    dayHours: 80,
    nightHours: 80,
  },
  {
    id: 'night-sixone',
    title: 'Ночные, 1 выходной',
    badge: '6/1',
    desc: '24 дня · 4 дн. + 4 ноч.',
    icon: 'ri-moon-foggy-line',
    accent: 'violet',
    dayHours: 96,
    nightHours: 96,
  },
];

const ACCENT_MAP: Record<string, { text: string; bg: string; border: string; ring: string; dot: string }> = {
  amber:   { text: 'text-accent-200',   bg: 'bg-accent-200/10',   border: 'border-accent-200/40',   ring: 'ring-accent-200/30',   dot: 'bg-accent-200'   },
  emerald: { text: 'text-primary-300', bg: 'bg-primary-300/10', border: 'border-primary-300/40', ring: 'ring-primary-300/30', dot: 'bg-primary-500' },
  indigo:  { text: 'text-night',  bg: 'bg-night/10',  border: 'border-night/40',  ring: 'ring-night/30',  dot: 'bg-night'  },
  violet:  { text: 'text-night-alt',  bg: 'bg-night-alt/10',  border: 'border-night-alt/40',  ring: 'ring-night-alt/30',  dot: 'bg-night-alt'  },
};

export default function SalaryCalculator() {
  const { config } = useCabinet();
  const { day: DAY_RATE, night: NIGHT_RATE, taxRate: TAX_RATE, monthlyHoursLimit: HOURS_LIMIT } = config.rates;

  const calcNet = (d: number, n: number) => {
    const gross = d * DAY_RATE + n * NIGHT_RATE;
    return gross - Math.round(gross * TAX_RATE);
  };

  const [dayHours, setDayHours] = useState(160);
  const [nightHours, setNightHours] = useState(0);
  const [activeSchedule, setActiveSchedule] = useState<string | null>('classic');

  const applySchedule = (s: typeof SCHEDULES[0]) => {
    setDayHours(s.dayHours);
    setNightHours(s.nightHours);
    setActiveSchedule(s.id);
  };

  const handleDayChange = (val: number) => { setDayHours(val); setActiveSchedule(null); };
  const handleNightChange = (val: number) => { setNightHours(val); setActiveSchedule(null); };

  const gross = useMemo(() => dayHours * DAY_RATE + nightHours * NIGHT_RATE, [dayHours, nightHours, DAY_RATE, NIGHT_RATE]);
  const tax   = useMemo(() => Math.round(gross * TAX_RATE), [gross, TAX_RATE]);
  const net   = useMemo(() => gross - tax, [gross, tax]);
  const totalHours = dayHours + nightHours;
  const isOverLimit = totalHours > HOURS_LIMIT;

  const dayPct   = Math.round((dayHours   / HOURS_LIMIT) * 100);
  const nightPct = Math.round((nightHours / HOURS_LIMIT) * 100);

  return (
    <div className="max-w-4xl space-y-6">

      {/* Header */}
      <div>
        <SectionTitle lead="Калькулятор" accent="заработной платы" />
        <p className="text-foreground-500 text-sm">Выберите график или настройте вручную — расчёт с учётом НПД 6%</p>
      </div>

      {/* Schedules grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {SCHEDULES.map((s) => {
          const a = ACCENT_MAP[s.accent];
          const isActive = activeSchedule === s.id;
          const estNet = calcNet(s.dayHours, s.nightHours);
          return (
            <button
              key={s.id}
              onClick={() => applySchedule(s)}
              className={`relative text-left rounded-2xl border p-4 transition-all cursor-pointer group
                ${isActive
                  ? `${a.bg} ${a.border} ring-1 ${a.ring}`
                  : 'bg-surface-1 border-surface-3 hover:border-foreground-400 hover:bg-surface-2/80'
                }`}
            >
              {isActive && (
                <span className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center">
                  <i className={`ri-check-line text-sm ${a.text}`} />
                </span>
              )}
              <span className={`w-8 h-8 flex items-center justify-center rounded-xl mb-3 ${a.bg}`}>
                <i className={`${s.icon} text-base ${a.text}`} />
              </span>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-sm font-semibold text-ink leading-tight">{s.title}</span>
              </div>
              <div className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-2 ${a.bg} ${a.text}`}>
                {s.badge}
              </div>
              <p className="text-[11px] text-foreground-500 mb-3 leading-snug">{s.desc}</p>
              <div className="border-t border-white/5 pt-3">
                <p className="text-[10px] text-foreground-400 mb-0.5">Ожидаемый доход</p>
                <p className={`text-sm font-bold ${a.text}`}>
                  ~{estNet.toLocaleString('ru-RU')} ₽
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sliders + Result */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Sliders */}
        <div className="lg:col-span-3 bg-surface-1 border border-surface-3 rounded-2xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">Параметры расчёта</h3>
            <span className={`text-xs px-2.5 py-1 rounded-full ${isOverLimit ? 'bg-red-500/10 text-red-400' : 'bg-white/5 text-foreground-500'}`}>
              {totalHours} / 300 ч
            </span>
          </div>

          {/* Total bar */}
          <div>
            <div className="h-2 bg-background-50 rounded-full overflow-hidden flex gap-0.5">
              <div
                className="h-full bg-accent-200 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(dayPct, 100)}%` }}
              />
              <div
                className="h-full bg-night rounded-full transition-all duration-300"
                style={{ width: `${Math.min(nightPct, 100 - Math.min(dayPct, 100))}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-accent-200" />
                <span className="text-[11px] text-foreground-500">Дневные</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-night" />
                <span className="text-[11px] text-foreground-500">Ночные</span>
              </div>
            </div>
          </div>

          {/* Day slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-200/10">
                  <i className="ri-sun-line text-sm text-accent-200" />
                </span>
                <span className="text-sm text-foreground-700">Дневные часы</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-accent-200">{dayHours}</span>
                <span className="text-xs text-foreground-500">ч</span>
                <span className="text-xs text-accent-400/70 ml-1">{(dayHours * DAY_RATE).toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <input
              type="range" min={0} max={300} value={dayHours}
              onChange={(e) => handleDayChange(Number(e.target.value))}
              className="w-full accent-accent-200 cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-foreground-300 mt-1.5">
              <span>0</span><span>150</span><span>300 ч</span>
            </div>
          </div>

          {/* Night slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-night/10">
                  <i className="ri-moon-line text-sm text-night" />
                </span>
                <span className="text-sm text-foreground-700">Ночные часы</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-night">{nightHours}</span>
                <span className="text-xs text-foreground-500">ч</span>
                <span className="text-xs text-night/70 ml-1">{(nightHours * NIGHT_RATE).toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <input
              type="range" min={0} max={300} value={nightHours}
              onChange={(e) => handleNightChange(Number(e.target.value))}
              className="w-full accent-night cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-foreground-300 mt-1.5">
              <span>0</span><span>150</span><span>300 ч</span>
            </div>
          </div>

          {isOverLimit && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5">
              <i className="ri-error-warning-line text-red-400 text-sm" />
              <span className="text-xs text-red-400">
                Превышен максимум 300 ч/мес (10 ч/сут × 30 дней). Суммарно: {totalHours} ч.
              </span>
            </div>
          )}
        </div>

        {/* Result */}
        <div className="lg:col-span-2 bg-surface-0 border border-primary-900/30 rounded-2xl p-6 flex flex-col justify-between">
          <div>
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-widest mb-5">Прогноз выплаты</p>

            <div className="space-y-3 mb-5">
              <div className="flex justify-between items-center">
                <span className="text-sm text-foreground-500">Итого часов</span>
                <span className={`text-sm font-semibold ${isOverLimit ? 'text-red-400' : 'text-foreground-700'}`}>
                  {totalHours} ч
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-foreground-500">Брутто</span>
                <span className="text-sm font-semibold text-foreground-700">{gross.toLocaleString('ru-RU')} ₽</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-foreground-500">НПД 6%</span>
                <span className="text-sm font-semibold text-red-400">−{tax.toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>

            <div className="border-t border-primary-900/30 pt-5 mb-5">
              <p className="text-xs text-primary-700 mb-1.5">К выплате на руки</p>
              <div className="flex items-end gap-1.5">
                <span className="text-4xl font-bold text-primary-300 leading-none">
                  {net.toLocaleString('ru-RU')}
                </span>
                <span className="text-xl text-primary-500 pb-0.5">₽</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-black/20 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-foreground-400 mb-0.5">Доход/час</p>
              <p className="text-sm font-semibold text-ink">
                {totalHours > 0 ? Math.round(net / totalHours).toLocaleString('ru-RU') : 0} ₽
              </p>
            </div>
            <div className="bg-black/20 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-foreground-400 mb-0.5">За год</p>
              <p className="text-sm font-semibold text-ink">
                {(net * 12).toLocaleString('ru-RU')} ₽
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="flex items-start gap-3 bg-surface-1 border border-surface-3 rounded-2xl px-5 py-4">
        <span className="w-5 h-5 flex items-center justify-center text-foreground-400 flex-shrink-0 mt-0.5">
          <i className="ri-scales-line text-sm" />
        </span>
        <p className="text-xs text-foreground-400 leading-relaxed">
          Расчёт является приблизительным прогнозом. Налог НПД 6% взимается с каждого поступления.
          Ставки: дневная (06:00–22:00 МСК) — 380 ₽/ч, ночная (22:00–06:00 МСК) — 420 ₽/ч.
        </p>
      </div>

    </div>
  );
}
